import React, { useEffect, useRef, useState } from 'react';
import { useUser } from '../context/UserContext';

const ADSENSE_CLIENT_ID = process.env.REACT_APP_ADSENSE_CLIENT_ID;
const ADSENSE_SLOT_ID = process.env.REACT_APP_ADSENSE_SLOT_ID;
const ADSENSE_SCRIPT_ID = 'adsense-script';

function ensureAdSenseScript() {
    if (typeof document === 'undefined' || !ADSENSE_CLIENT_ID) {
        return;
    }

    const existingScript = document.getElementById(ADSENSE_SCRIPT_ID);
    if (existingScript) {
        return;
    }

    const script = document.createElement('script');
    script.id = ADSENSE_SCRIPT_ID;
    script.async = true;
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT_ID}`;
    script.crossOrigin = 'anonymous';

    // Attach handlers to detect load/error for diagnostics and telemetry
    script.onload = () => {
        script.dataset.adsenseLoaded = 'true';
        try { window._adsenseLoadOk = true; } catch (e) {}
        try { postEvent('load_success', ADSENSE_SLOT_ID || 'unknown'); } catch (e) {}
    };
    script.onerror = (err) => {
        script.dataset.adsenseLoadFailed = 'true';
        console.error('AdSense script failed to load', err);
        try { postEvent('load_error', ADSENSE_SLOT_ID || 'unknown'); } catch (e) {}
    };

    document.head.appendChild(script);
}

async function postEvent(type, adSlot) {
    try {
        const token = localStorage.getItem('token');
        await fetch('/api/ads/event', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { 'x-auth-token': token } : {}),
            },
            body: JSON.stringify({ type, adSlot }),
        });
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.warn('Failed to post ad event', err);
    }
}

function AdSenseAd({ className = '', minHeight = 250, label = 'Advertisement', slotId = ADSENSE_SLOT_ID }) {
    const pushedRef = useRef(false);
    const { user } = useUser();
    const isConfigured = Boolean(ADSENSE_CLIENT_ID && slotId);

    // Derive consent: prefer server-side stored user.adConsent, fallback to localStorage
    const consent = (user && (user.adConsent === 'granted' || user.adConsent === 'denied')) ? user.adConsent : (typeof window !== 'undefined' ? localStorage.getItem('ads_consent') : null);

    const [isVisible, setIsVisible] = useState(false);
    const slotRef = useRef(null);

    // Observe viewport to lazy-load ads
    useEffect(() => {
        if (typeof window === 'undefined' || !slotRef.current) return undefined;
        if (typeof IntersectionObserver === 'undefined') {
            // Fallback: assume visible
            setIsVisible(true);
            return undefined;
        }

        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        io.disconnect();

                        // Record impression with session dedupe
                        try {
                            const key = `ad_impression_${slotId}`;
                            const last = sessionStorage.getItem(key);
                            const now = Date.now();
                            if (!last || now - Number(last) > 60 * 1000) {
                                sessionStorage.setItem(key, String(now));
                                postEvent('impression', slotId);
                            }
                        } catch (err) {
                            // ignore storage errors
                        }
                    }
                });
            },
            { rootMargin: '200px' }
        );

        io.observe(slotRef.current);
        return () => io.disconnect();
    }, [slotId]);

    // Hooks must run unconditionally and in the same order on every render
    useEffect(() => {
        if (!isVisible) return;
        if (consent !== 'granted') return;
        ensureAdSenseScript();
    }, [consent, isVisible]);

    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined' || !isConfigured || pushedRef.current || !isVisible) {
            return;
        }

        const effectiveConsent = (user && (user.adConsent === 'granted' || user.adConsent === 'denied')) ? user.adConsent : (consent || (process.env.NODE_ENV !== 'production' ? 'granted' : null));
        if (effectiveConsent !== 'granted') return;

        // If script previously failed to load, bail out early
        const existingScript = typeof document !== 'undefined' ? document.getElementById(ADSENSE_SCRIPT_ID) : null;
        if (existingScript && existingScript.dataset && existingScript.dataset.adsenseLoadFailed === 'true') {
            setLoadFailed(true);
            try { postEvent('load_error', slotId); } catch (e) {}
            return;
        }

        window.adsbygoogle = window.adsbygoogle || [];

        let checkTimer;
        try {
            window.adsbygoogle.push({});
            pushedRef.current = true;
            // If adsbygoogle isn't functional within a reasonable time, mark as failed
            checkTimer = setTimeout(() => {
                if (!(window.adsbygoogle && typeof window.adsbygoogle.push === 'function')) {
                    setLoadFailed(true);
                    try { postEvent('load_error', slotId); } catch (e) {}
                }
            }, 2500);
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                console.warn('AdSense failed to initialize.', err);
            }
            setLoadFailed(true);
            try { postEvent('load_error', slotId); } catch (e) {}
        }

        return () => { if (checkTimer) clearTimeout(checkTimer); };
    }, [isConfigured, consent, isVisible, slotId, user]);

    // in dev, treat failure as acceptable and show mock
    const showDevMock = !isConfigured || (process.env.NODE_ENV !== 'production' && loadFailed);

    // Respect user account ad-free purchases
    if (user?.hasNoAds) return null;

    // Respect stored consent
    if (consent === 'denied') return null;
    if (!consent && process.env.NODE_ENV === 'production') {
        // No consent yet in production: do not load ads
        return null;
    }

    if (!isConfigured && process.env.NODE_ENV === 'production') return null;

    if (showDevMock) {
        return (
            <aside ref={slotRef} className={`ad-slot ad-slot--mock ${className}`.trim()} aria-label={label} onClick={() => postEvent('click', slotId)}>
                <div className="ad-slot__label">{label} (demo)</div>
                <div className="ad-slot__placeholder" style={{ minHeight: `${minHeight}px`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{border:'1px dashed var(--color-border)',padding:12,borderRadius:8,background:'transparent'}}>Ad placeholder — ads are not available in this environment.</div>
                </div>
            </aside>
        );
    }

    const handleClick = () => {
        // best-effort click tracking
        try { postEvent('click', slotId); } catch (err) {}
    };

    return (
        <aside ref={slotRef} className={`ad-slot ${className}`.trim()} aria-label={label} onClick={handleClick}>
            <div className="ad-slot__label">{label}</div>
            {!isVisible ? (
                <div className="ad-slot__placeholder" style={{ minHeight: 20 }} aria-hidden="true" />
            ) : (
                <ins
                    className="adsbygoogle ad-slot__unit"
                    style={{ display: 'block', minHeight: `${minHeight}px` }}
                    data-ad-client={ADSENSE_CLIENT_ID}
                    data-ad-slot={slotId}
                    data-ad-format="auto"
                    data-full-width-responsive="true"
                />
            )}
        </aside>
    );
}

export default AdSenseAd;
